import {
  Button,
  Divider,
  Fade,
  List,
  ListItem,
  Popper,
  Typography
} from "@material-ui/core";
import { PopperProps } from "@material-ui/core/Popper";
import { makeStyles } from "@material-ui/core/styles";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import classNames from "classnames";
import React, { FC, Fragment, useRef, useState } from "react";

const useStyles = makeStyles(
  theme => ({
    container: {
      backgroundColor: "#FFD15C",
      display: "flex",
      padding: theme.spacing(0.75, 0.5, 0.75, 1.5)
    },
    divider: {
      backgroundColor: theme.palette.primary.dark,
      borderRadius: theme.spacing(1),
      height: theme.spacing(2),
      margin: theme.spacing(0, 0.5, 0, 1),
      width: "2px"
    },
    dropdownIcon: {
      marginLeft: "0 !important"
    },
    list: {
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.spacing(0.5),
      boxShadow: theme.shadows[8],
      gridRowGap: 0,
      padding: "4px 0 !important"
    },
    listItemButton: {
      padding: theme.spacing(1, 2)
    },
    listItemText: {
      color: theme.palette.secondary.main
    },
    listItemTextDisabled: {
      color: theme.palette.text.disabled
    }
  }),
  { name: "ButtonMultiAction" }
);

export interface Action {
  action: () => any;
  label: string;
  disabled?: boolean;
  className?: string;
}

interface ButtonMultiActionProps {
  actions: Action[];
  disablePortal?: PopperProps["disablePortal"];
  label: string;
  modifiers?: PopperProps["modifiers"];
  placement?: PopperProps["placement"];
  transitionTimeout?:
    | number
    | {
        appear?: number;
        enter?: number;
        exit?: number;
      };
  btnClassStyle?: string;
  itemBtnClassStyle?: string;
  hideDivider?: boolean;
  forceVisibility?: boolean;
}

const ButtonMultiAction: FC<ButtonMultiActionProps> = ({
  actions,
  disablePortal,
  label,
  placement,
  modifiers,
  transitionTimeout,
  btnClassStyle,
  itemBtnClassStyle,
  hideDivider,
  forceVisibility = false
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const classes = useStyles({});

  const handleClick = () => {
    setOpen(prev => !prev);
  };

  React.useEffect(() => {
    setOpen(forceVisibility);
  }, [forceVisibility]);

  return (
    <>
      <Button
        aria-describedby="action-items"
        className={btnClassStyle || classes.container}
        color="primary"
        onClick={handleClick}
        ref={buttonRef}
      >
        <Typography variant="button">{label}</Typography>
        {hideDivider ? <></> : <div className={classes.divider}></div>}
        <ArrowDropDownIcon className={classes.dropdownIcon} />
      </Button>

      <Popper
        anchorEl={buttonRef.current}
        disablePortal={disablePortal}
        id="action-items"
        open={open}
        modifiers={{
          flip: {
            enabled: false
          },
          hide: {
            enabled: false
          },
          preventOverflow: {
            enabled: false
          },
          ...modifiers
        }}
        placement={placement}
        transition
        style={{ zIndex: 1 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={transitionTimeout}>
            <List className={classes.list} component="nav">
              {actions?.map(
                (
                  {
                    label,
                    action,
                    disabled: listItemDisabled = false,
                    className
                  },
                  index
                ) => (
                  <Fragment key={index}>
                    {hideDivider || index === 0 ? <></> : <Divider />}
                    <ListItem
                      button
                      disabled={listItemDisabled}
                      className={className || classes.listItemButton}
                      onClick={action}
                    >
                      <Typography
                        className={
                          itemBtnClassStyle ||
                          classNames(classes.listItemText, {
                            [classes.listItemTextDisabled]: listItemDisabled
                          })
                        }
                        variant="button"
                      >
                        {label}
                      </Typography>
                    </ListItem>
                  </Fragment>
                )
              )}
            </List>
          </Fade>
        )}
      </Popper>
    </>
  );
};

ButtonMultiAction.defaultProps = {
  disablePortal: false,
  placement: "bottom-end",
  transitionTimeout: {
    enter: 225,
    exit: 195
  }
};

export default ButtonMultiAction;
